import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { contractApi } from "../../services/contractApi";
import { EagleState } from "../../components/EagleState";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { useLocalization } from "../../hooks/useLocalization";

export default function ContractTemplatesPage() {
  const { t } = useLocalization();
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", content_en: "", content_ja: "" });
  const [accessDenied, setAccessDenied] = useState(false);

  const load = () => contractApi.listTemplates()
    .then((r) => { setTemplates(r.data); setAccessDenied(false); })
    .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); });
  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await contractApi.createTemplate(form);
    setForm({ name: "", content_en: "", content_ja: "" });
    setShowForm(false);
    load();
  };

  if (accessDenied) return <AccessDenied resourceLabel={t("contracts.title")} />;

  return (
    <div className="max-w-3xl" data-testid="contract-templates-page">
      <PageHelpBanner pageKey="contract-templates" title="How to use this page">
        <p>Contract templates use merge fields like <code>{"{{legal_name}}"}</code>, <code>{"{{position}}"}</code>, and <code>{"{{store_location}}"}</code> - these get filled in automatically when a contract is generated for a specific employee. Create a new version here; the employee onboarding flow always uses the active template.</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("contracts.title")}</h2>
        <button
          data-testid="new-contract-template-button"
          onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all"
        >
          <Plus size={16} /> {t("contracts.newTemplate")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4 mb-6">
          <input data-testid="template-name-input" required placeholder={t("contracts.templateName")} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          <textarea data-testid="template-content-en-input" required placeholder={`${t("contracts.contentEn")} - use {{legal_name}}, {{position}}, {{store_location}}`} value={form.content_en} onChange={(e) => setForm((f) => ({ ...f, content_en: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" rows={4} />
          <textarea data-testid="template-content-ja-input" required placeholder={t("contracts.contentJa")} value={form.content_ja} onChange={(e) => setForm((f) => ({ ...f, content_ja: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" rows={4} />
          <button data-testid="submit-template-button" type="submit" className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all">{t("common.submit")}</button>
        </form>
      )}

      <div className="space-y-3">
        {templates.map((tpl) => (
          <div key={tpl.id} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`template-item-${tpl.id}`}>
            <p className="font-semibold text-gray-800 text-sm">{tpl.name}</p>
            <p className="text-xs text-gray-500">v{tpl.version} · {tpl.status}</p>
          </div>
        ))}
        {templates.length === 0 && <EagleState variant="empty" title={t("eagle.noContractsTitle")} message={t("eagle.noContractsMessage")} testId="contract-templates-empty" />}
      </div>
    </div>
  );
}
